/**
 * ImmersiveWorkoutBreakOverlay - Break timer with up-next preview
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Theme } from '../../../../Theme/Theme';
import { ViewSet } from '../../../../Model/WorkoutLog/ViewSet';
import { useLanguage } from '../../../../Hooks/useLanguage';
import { useTrans } from '../../../../Store/System/LocalizationStore';
import { CircularProgress } from './CircularProgress';
import { getMediaLargeOrMediumOrSmallest } from '../../../../Model/Media/Media';

const pulseAnimation = keyframes`
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.8; }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 200;
  display: flex;
  flex-direction: column;
`;

const GlowBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${Theme.Colors.primary}15;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
  pointer-events: none;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 24px;
  padding-top: max(120px, calc(env(safe-area-inset-top) + 100px));
`;

const TimerSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const TimerLabel = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
`;

const TimerValue = styled.div`
  color: #fff;
  font-size: 64px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
`;

const TimerSubtext = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin-top: 8px;
`;

const BottomSection = styled.div`
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
`;

const UpNextCard = styled.div`
  background: rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const UpNextLabel = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 1px;
  margin-bottom: 12px;
`;

const UpNextContent = styled.div`
  display: flex;
  align-items: center;
`;

const UpNextImage = styled.div<{ src?: string }>`
  width: 64px;
  height: 64px;
  border-radius: 12px;
  background-color: rgba(255, 255, 255, 0.1);
  background-image: ${({ src }) => (src ? `url(${src})` : 'none')};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const UpNextInfo = styled.div`
  flex: 1;
  margin-left: 16px;
`;

const UpNextExercise = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 16px;
`;

const UpNextDetails = styled.div`
  color: ${Theme.Colors.primary};
  margin-top: 4px;
  font-weight: 600;
  font-size: 13px;
`;

const SkipButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 32px;
  border-radius: 32px;
  border: none;
  background: ${Theme.Colors.primary};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 12px ${Theme.Colors.primary}66;
  margin-bottom: max(24px, env(safe-area-inset-bottom));
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  & > i {
    font-size: 24px;
  }
`;

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${seconds}`;
}

export type ImmersiveWorkoutBreakOverlayProps = {
  viewSet: ViewSet;
  nextSet: ViewSet;
  isAutoMode: boolean;
  onSkipBreak: () => void;
};

export const ImmersiveWorkoutBreakOverlay: React.FC<ImmersiveWorkoutBreakOverlayProps> = observer(
  ({ viewSet, nextSet, isAutoMode, onSkipBreak }) => {
    const { t } = useTrans();
    const { language } = useLanguage();
    const autoAdvancedRef = React.useRef(false);

    const breakTime = viewSet.breakTime;
    const remainingBreak = viewSet.remainingBreak;
    const plannedBreak = viewSet.plannedBreakTime * 1000;
    const hasPlannedBreak = plannedBreak > 0;

    // Auto-advance to next exercise when break completes in auto mode
    React.useEffect(() => {
      if (!isAutoMode || !hasPlannedBreak) return;
      if (remainingBreak <= 0 && !autoAdvancedRef.current) {
        autoAdvancedRef.current = true;
        onSkipBreak();
      }
    }, [isAutoMode, hasPlannedBreak, remainingBreak, onSkipBreak]);

    // Reset auto-advance flag when viewSet changes (new break starts)
    React.useEffect(() => {
      autoAdvancedRef.current = false;
    }, [viewSet]);

    const timerText = hasPlannedBreak ? formatDuration(Math.max(0, remainingBreak)) : formatDuration(breakTime);
    const timerSubtext = hasPlannedBreak
      ? remainingBreak > 0
        ? t('workout.break.remaining', { defaultValue: 'remaining' })
        : t('workout.break.complete', { defaultValue: 'rest complete' })
      : t('workout.break.elapsed', { defaultValue: 'elapsed' });

    const progress = hasPlannedBreak ? Math.max(0, remainingBreak / plannedBreak) : 0;

    const nextExerciseName = nextSet.exercise.localeName(language);
    const exerciseMedia = nextSet.viewBlock.exerciseMedia;
    const nextExerciseImage = exerciseMedia ? getMediaLargeOrMediumOrSmallest(exerciseMedia) : undefined;
    const isSameExercise = nextSet.viewBlock.id === viewSet.viewBlock.id;

    const buttonText = hasPlannedBreak && remainingBreak > 0
      ? t('workout.skipBreak', { defaultValue: 'SKIP BREAK' })
      : t('workout.startNext', { defaultValue: 'START NEXT SET' });

    return (
      <Overlay>
        <GlowBackground />
        <Content>
          <TimerSection>
            <TimerLabel>{t('workout.restTime', { defaultValue: 'REST TIME' })}</TimerLabel>
            {hasPlannedBreak ? (
              <CircularProgress size={200} strokeWidth={6} progress={progress}>
                <TimerValue>{timerText}</TimerValue>
              </CircularProgress>
            ) : (
              <TimerValue>{timerText}</TimerValue>
            )}
            <TimerSubtext>{timerSubtext}</TimerSubtext>
          </TimerSection>

          <BottomSection>
            <UpNextCard>
              <UpNextLabel>
                {isSameExercise
                  ? t('workout.nextSet', { defaultValue: 'NEXT SET' })
                  : t('workout.upNext', { defaultValue: 'UP NEXT' })}
              </UpNextLabel>
              <UpNextContent>
                <UpNextImage src={nextExerciseImage} />
                <UpNextInfo>
                  <UpNextExercise>{nextExerciseName}</UpNextExercise>
                  <UpNextDetails>
                    {nextSet.durationBased
                      ? `${nextSet.plannedDuration}s`
                      : nextSet.plannedRepetitions
                        ? `${nextSet.plannedRepetitions} reps`
                        : ''}
                  </UpNextDetails>
                </UpNextInfo>
              </UpNextContent>
            </UpNextCard>

            <SkipButton onClick={onSkipBreak}>
              {buttonText}
              <i className="mdi mdi-play" />
            </SkipButton>
          </BottomSection>
        </Content>
      </Overlay>
    );
  },
);
